import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { notification } from 'antd';
import { NOT_FOUND_HASH } from 'common/constants';
import { AppRoutes } from 'common/types';
import isEmpty from 'lodash.isempty';
import { delay } from 'common/helpers/delay';
import {
  generalSliceActions,
  generalSliceSelectors,
} from 'store/slices/general/config';
import { usersActions, usersSelectors } from 'store/slices/users/config';
import { useAppDispatch, useAppSelector } from 'store';
import type { Translation } from 'store/slices/general/general.slice';
import queryParams from 'query-string';
import { checkJwtTokenIsValid } from 'common/helpers/checkJwtTokenIsValid';
import { carsActions, carsSelectors } from 'store/slices';
import { useTranslations } from './useTranslations';
interface UseApp {
  translationsIsPending: boolean;
  translations: Array<Translation>;
  contextHolder: ReactElement;
  hideNavigation: boolean;
}
export const useApp = (): UseApp => {
  const location = useLocation();
  const appMessage = useAppSelector(
    generalSliceSelectors.selectGeneralMessages,
  );
  const { t } = useTranslations();
  const selectedCar = useAppSelector(carsSelectors.selectSelectedCar);
  const [api, contextHolder] = notification.useNotification();
  const translations = useAppSelector(
    generalSliceSelectors.selectAllTranslations,
  );
  const isAuthenticated = useAppSelector(usersSelectors.selectUserAuthStatus);
  const translationsIsPending = useAppSelector(
    generalSliceSelectors.selectTranslationsPendingStatus,
  );
  const [, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(usersSelectors.selectProfile);
  const [hideNavigationPanel, setHideNavigationPanel] = useState(
    location.hash.includes(NOT_FOUND_HASH),
  );

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const hideNavigation =
    location.pathname.includes(AppRoutes.ADMIN) ||
    location.pathname.includes(AppRoutes.CONFIRM_ORDER_PAYMENT) ||
    location.pathname.includes(AppRoutes.CONFIRM_ACCOUNT) ||
    location.pathname.includes(AppRoutes.RESET_PASSWORD) ||
    hideNavigationPanel;
  useEffect(() => {
    if (location.hash.includes(NOT_FOUND_HASH)) {
      setHideNavigationPanel(true);
    }
    if (!location.hash.includes(NOT_FOUND_HASH)) {
      setHideNavigationPanel(false);
    }
  }, [location.hash]);

  useEffect(() => {
    const tokenFromQuery = searchParams.get('token');
    if (tokenFromQuery && location.pathname !== AppRoutes.CONFIRM_ACCOUNT) {
      navigate(`${AppRoutes.RESERVATION}`);
    }
  }, [location.pathname, navigate, searchParams]);

  useEffect(() => {
    if (localStorage.token && isEmpty(profile)) {
      dispatch(usersActions.getMe());
    }
  }, [dispatch, profile]);

  useEffect(() => {
    if (appMessage) {
      api[appMessage.type]({
        message: appMessage.message,
        placement: 'bottomRight',
      });
      (async () => {
        await delay(5000);
        dispatch(generalSliceActions.setAppMessage(null));
      })();
    }
  }, [api, appMessage, dispatch]);

  useEffect(() => {
    dispatch(generalSliceActions.getSupportEmails());
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(translations)) {
      dispatch(generalSliceActions.getAllTranslations());
    }
  }, [dispatch, translations]);

  useEffect(() => {
    const parsedQuery = queryParams.parse(window.location.search);
    if (
      !isEmpty(profile) &&
      !profile.isVerified &&
      isEmpty(selectedCar) &&
      !parsedQuery.verificationToken
    ) {
      navigate(AppRoutes.EMAIL_VERIFICATION);
    }
  }, [navigate, profile, selectedCar]);
  useEffect(() => {
    const parsedQuery = queryParams.parse(window.location.search);
    if (
      parsedQuery.verificationToken &&
      typeof parsedQuery.verificationToken === 'string' &&
      isAuthenticated &&
      !isEmpty(translations) &&
      checkJwtTokenIsValid(parsedQuery.verificationToken as string)
    ) {
      setSearchParams({});
      dispatch(
        usersActions.verifyAccount(
          parsedQuery.verificationToken,
          `${t('DEAR_FULL_NAME', [
            {
              key: 'firstName',
              value: String(profile?.firstName),
            },
            {
              key: 'lastName',
              value: String(profile?.lastName),
            },
          ])} ${t('VERIFICATION_SUCCESS')}`,
          () => {
            setSearchParams({});
          },
        ),
      );
    }
    //eslint-disable-next-line
  }, [dispatch, translations, isAuthenticated]);

  useEffect(() => {
    dispatch(carsActions.getCars());
    dispatch(usersActions.getAllUsers({}));
  }, [dispatch]);
  return {
    translationsIsPending,
    translations,
    contextHolder,
    hideNavigation,
  };
};
